import { isPlatformBrowser } from '@angular/common';
import {
  HttpErrorResponse,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';

import { catchError, finalize, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { apiList } from '../../services/apiList.service';
import { EncryptionService } from '../../services/encryption.service';
import { ServerClockService } from '../../services/server-clock.service';
import { buildHmacHeaders, isTimestampDriftError, shouldAttachHmac } from '../helpers/hmac.helper';

const pendingRequests = new Set<string>();

function withHmacHeaders(
  headers: HttpHeaders,
  req: HttpRequest<unknown>,
  timestamp: string,
  url: string,
  body: unknown,
): HttpHeaders {
  if (!environment.hmacSecretKey || !shouldAttachHmac(url, environment.base_url)) {
    return headers;
  }

  const hmacHeaders = buildHmacHeaders({
    method: req.method,
    url,
    body,
    secret: environment.hmacSecretKey,
    timestamp,
  });

  return headers
    .set('x-signature', hmacHeaders['x-signature'])
    .set('x-timestamp', hmacHeaders['x-timestamp']);
}

function withGoogleDirectionsHash(
  headers: HttpHeaders,
  encryptionService: EncryptionService,
  nowMs: number,
): HttpHeaders {
  return headers.set(
    'hash',
    encryptionService.encryptData({
      appkey: new Date(nowMs).toISOString(),
    }),
  );
}

export const setInterceptor: HttpInterceptorFn = (req, next) => {
  const encryptionService = inject(EncryptionService);
  const apiListService = inject(apiList);
  const serverClock = inject(ServerClockService);
  const platformId = inject(PLATFORM_ID);

  const isBrowser = isPlatformBrowser(platformId);
  if (!isBrowser) {
    return next(req);
  }

  const token = encryptionService.decryptLocalStorage(environment.storageKey) ?? '';
  const fcm = encryptionService.decryptLocalStorage('fcmToken') ?? '';
  const language = localStorage['language'] ?? 'en';
  const timezone = serverClock.timezone();

  const headersObj: Record<string, string> = {
    'x-device-type': 'WEB',
    'x-device-token': fcm,
    'Accept-Language': language,
    'x-portal': 'CLIENT',
    'cache-control': 'no-cache, max-age=0',
    'ngsw-cache': 'false',
    Pragma: 'no-cache',
    'x-timezone': timezone,
  };

  if (token) {
    headersObj['authorization'] = `Bearer ${token}`;
  }

  let headers = req.headers;
  Object.entries(headersObj).forEach(([k, v]) => {
    headers = headers.set(k, v);
  });

  const isGoogleDirectionApi = req.url.includes(
    environment.base_url + apiListService.googleDirections,
  );

  const attachHmac =
    !!environment.hmacSecretKey && shouldAttachHmac(req.urlWithParams, environment.base_url);

  const dispatch = (request: HttpRequest<unknown>, allowRetry: boolean) =>
    next(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!allowRetry || !attachHmac || !isTimestampDriftError(error)) {
          return throwError(() => error);
        }

        return from(serverClock.sync()).pipe(
          switchMap(() => {
            const timestampMs = serverClock.nowMs();
            let retriedHeaders = request.headers.set('x-timezone', serverClock.timezone());

            if (isGoogleDirectionApi) {
              retriedHeaders = withGoogleDirectionsHash(
                retriedHeaders,
                encryptionService,
                timestampMs,
              );
            }

            const retried = request.clone({
              headers: withHmacHeaders(
                retriedHeaders,
                request,
                timestampMs.toString(),
                request.urlWithParams,
                request.body,
              ),
            });
            return dispatch(retried, false);
          }),
        );
      }),
    );

  let cloned: HttpRequest<unknown>;

  if (isGoogleDirectionApi) {
    const timestampMs = serverClock.nowMs();
    const encryptedBody = encryptionService.encryptData(req.body);
    const wireBody = { hash: encryptedBody };

    cloned = req.clone({
      headers: withHmacHeaders(
        withGoogleDirectionsHash(headers, encryptionService, timestampMs),
        req,
        timestampMs.toString(),
        req.urlWithParams,
        wireBody,
      ),
      body: wireBody,
    });
  } else {
    if (!(req.body instanceof FormData)) {
      headers = headers.set('Content-Type', 'application/json');
    }

    headers = withHmacHeaders(
      headers,
      req,
      serverClock.nowMs().toString(),
      req.urlWithParams,
      req.body,
    );

    cloned = req.clone({ headers });
  }

  const isMutatingMethod = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(req.method);

  if (isMutatingMethod) {
    const requestKey = `${req.method}:${req.urlWithParams}:${
      req.body instanceof FormData ? 'form-data' : JSON.stringify(req.body)
    }`;

    if (pendingRequests.has(requestKey)) {
      return throwError(() => new Error('Duplicate Request'));
    }

    pendingRequests.add(requestKey);

    return dispatch(cloned, true).pipe(
      finalize(() => {
        pendingRequests.delete(requestKey);
      }),
    );
  }

  return dispatch(cloned, true);
};
